import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useReplyToTicket,
  useSupportTicketDetail,
  useUpdateTicket,
} from '@/hooks/useAdminSupport';
import type { SupportTicketReply } from '@/services/admin-support.service';
import { AgentBadge } from './SupportTicketsPage';
import { formatDateTime, formatRelativeTime } from '@/utils/formatters';
import { ArrowLeft, Bot, Clock, Download, FileText, ImageIcon, Mail, Paperclip, Send, Shield, Tag, User } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const statusLabels: Record<string, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  waiting_for_user: 'En attente du client',
  resolved: 'Résolu',
  closed: 'Fermé',
};

const statusVariants: Record<
  string,
  'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'
> = {
  open: 'danger',
  in_progress: 'warning',
  waiting_for_user: 'info',
  resolved: 'success',
  closed: 'neutral',
};

const priorityLabels: Record<string, string> = {
  low: 'Basse',
  normal: 'Normale',
  high: 'Haute',
  urgent: 'Urgente',
};

const priorityVariants: Record<
  string,
  'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'
> = {
  low: 'neutral',
  normal: 'info',
  high: 'warning',
  urgent: 'danger',
};

function isImageFile(filename: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(filename);
}

function AttachmentsList({ attachments }: { attachments: any[] | null | undefined }) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-medium text-neutral-500 flex items-center gap-1">
        <Paperclip className="h-3 w-3" />
        {attachments.length} pièce(s) jointe(s)
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {attachments.map((att: any, i: number) => {
          const hasUrl = att.url && att.url !== '';
          const isImage = isImageFile(att.filename || '');

          return (
            <div key={i} className="overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
              {isImage && hasUrl ? (
                <a href={att.url} target="_blank" rel="noopener noreferrer" className="block">
                  <img
                    src={att.url}
                    alt={att.filename}
                    className="h-40 w-full object-cover transition-opacity hover:opacity-80"
                  />
                </a>
              ) : isImage && !hasUrl ? (
                <div className="flex h-40 items-center justify-center bg-neutral-100 dark:bg-neutral-800">
                  <ImageIcon className="h-10 w-10 text-neutral-400" />
                </div>
              ) : null}
              <div className="flex items-center justify-between p-2 bg-neutral-50 dark:bg-neutral-800/50">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                  <span className="text-xs text-neutral-700 dark:text-neutral-300 truncate">{att.filename}</span>
                  {att.size > 0 && (
                    <span className="text-xs text-neutral-400 shrink-0">
                      ({att.size > 1024 * 1024 ? `${(att.size / 1024 / 1024).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`})
                    </span>
                  )}
                </div>
                {hasUrl && (
                  <a href={att.url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-primary-500 hover:text-primary-600">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SupportTicketDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading } = useSupportTicketDetail(uuid);
  const replyMutation = useReplyToTicket();
  const updateMutation = useUpdateTicket();
  const [replyContent, setReplyContent] = useState('');

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!ticket || !ticket.uuid) {
    return (
      <div className="py-12 text-center text-neutral-600 dark:text-neutral-400">
        Ticket introuvable
      </div>
    );
  }

  const handleReply = () => {
    if (!replyContent.trim()) return;
    replyMutation.mutate(
      { uuid: ticket.uuid, content: replyContent },
      { onSuccess: () => setReplyContent('') },
    );
  };

  const handleStatusChange = (status: string) => {
    updateMutation.mutate({ uuid: ticket.uuid, status });
  };

  const handlePriorityChange = (priority: string) => {
    updateMutation.mutate({ uuid: ticket.uuid, priority });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/support')}
          />
          <div>
            <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {ticket.subject}
            </h1>
            {ticket.user ? (
              <p className="text-sm text-neutral-500">
                par{' '}
                <Link
                  to={`/users/${ticket.user.uuid}`}
                  className="text-primary-500 hover:underline"
                >
                  {ticket.user.firstName} {ticket.user.lastName}
                </Link>{' '}
                ({ticket.user.email}) &mdash;{' '}
                {formatRelativeTime(ticket.createdAt)}
              </p>
            ) : (
              <p className="flex items-center gap-1 text-sm text-neutral-500">
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                {ticket.requesterName || ticket.requesterEmail || 'Demandeur inconnu'}
                {ticket.requesterName && ticket.requesterEmail
                  ? ` (${ticket.requesterEmail})`
                  : ''}{' '}
                &mdash; sans compte, reçu par e-mail &mdash;{' '}
                {formatRelativeTime(ticket.createdAt)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AgentBadge ticket={ticket} />
          <Badge variant={statusVariants[ticket.status]}>
            {statusLabels[ticket.status]}
          </Badge>
          <Badge variant={priorityVariants[ticket.priority]}>
            {priorityLabels[ticket.priority]}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Conversation */}
        <div className="space-y-4 lg:col-span-3">
          {/* Original message */}
          <Card>
            <CardContent>
              <div className="flex items-start gap-3 py-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {ticket.user
                        ? `${ticket.user.firstName} ${ticket.user.lastName}`
                        : ticket.requesterName || ticket.requesterEmail}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {formatDateTime(ticket.createdAt)}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300">
                    {ticket.content}
                  </div>
                  <AttachmentsList attachments={ticket.attachments} />
                </div>
              </div>
            </CardContent>
          </Card>

          {ticket.agentEtat === 'remonte' && (
            <Card>
              <CardContent>
                <aside
                  className="space-y-2 py-2"
                  aria-label="Remontée de l’assistant du support"
                >
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                    <Bot className="h-4 w-4" aria-hidden="true" />
                    L’assistant n’a pas répondu
                    {ticket.agentMotif ? ` : ${ticket.agentMotif}` : ''}
                  </p>
                  {ticket.agentBrouillon ? (
                    <>
                      <p className="text-xs font-medium uppercase text-neutral-500">
                        Son brouillon, non envoyé
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300">
                        {ticket.agentBrouillon}
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setReplyContent(ticket.agentBrouillon ?? '')}
                      >
                        Reprendre ce brouillon
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Aucun brouillon : la réponse est à écrire.
                    </p>
                  )}
                </aside>
              </CardContent>
            </Card>
          )}

          {/* Replies */}
          {ticket.replies?.map((reply: SupportTicketReply) => (
            <Card key={reply.uuid}>
              <CardContent>
                <div className="flex items-start gap-3 py-2">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      reply.authorRole === 'admin'
                        ? 'bg-orange-100 dark:bg-orange-900/30'
                        : reply.authorRole === 'assistant'
                          ? 'bg-primary-100 dark:bg-primary-900/30'
                          : 'bg-blue-100 dark:bg-blue-900/30'
                    }`}
                  >
                    {reply.authorRole === 'admin' ? (
                      <Shield className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    ) : reply.authorRole === 'assistant' ? (
                      <Bot className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {reply.authorName}
                      </span>
                      {reply.authorRole === 'admin' && (
                        <Badge variant="warning">Admin</Badge>
                      )}
                      {reply.authorRole === 'assistant' && (
                        <Badge variant="brand">Réponse automatique</Badge>
                      )}
                      {reply.authorRole === 'external' && (
                        <Badge variant="neutral">Par e-mail</Badge>
                      )}
                      <span className="text-xs text-neutral-500">
                        {formatDateTime(reply.createdAt)}
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300">
                      {reply.content}
                    </div>
                    <AttachmentsList attachments={reply.attachments} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Reply form */}
          <Card>
            <CardContent>
              <div className="space-y-3 py-2">
                <textarea
                  className="w-full resize-none rounded-lg border border-neutral-300 bg-white p-3 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100"
                  rows={4}
                  placeholder="Écrire une réponse..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button
                    icon={<Send className="h-4 w-4" />}
                    onClick={handleReply}
                    loading={replyMutation.isPending}
                    disabled={!replyContent.trim()}
                  >
                    Envoyer la réponse
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Actions" />
            <CardContent>
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase text-neutral-500">
                  Statut
                </p>
                <div className="flex flex-wrap gap-1">
                  {(
                    [
                      'open',
                      'in_progress',
                      'waiting_for_user',
                      'resolved',
                      'closed',
                    ] as const
                  ).map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      disabled={ticket.status === s}
                      className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                        ticket.status === s
                          ? 'cursor-default bg-primary-500 text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600'
                      }`}
                    >
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>

                <p className="mt-3 text-xs font-medium uppercase text-neutral-500">
                  Priorité
                </p>
                <div className="flex flex-wrap gap-1">
                  {(['low', 'normal', 'high', 'urgent'] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePriorityChange(p)}
                      disabled={ticket.priority === p}
                      className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                        ticket.priority === p
                          ? 'cursor-default bg-primary-500 text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600'
                      }`}
                    >
                      {priorityLabels[p]}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Détails" />
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                  <Clock className="h-3.5 w-3.5 text-neutral-400" />
                  <span>Créé le {formatDateTime(ticket.createdAt)}</span>
                </div>
                {ticket.agentTraiteLe && (
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                    <Bot className="h-3.5 w-3.5 text-neutral-400" />
                    <span>
                      Assistant : {formatDateTime(ticket.agentTraiteLe)}
                    </span>
                  </div>
                )}
                {ticket.firstResponseAt && (
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                    <Send className="h-3.5 w-3.5 text-neutral-400" />
                    <span>
                      1ère réponse{' '}
                      {formatRelativeTime(ticket.firstResponseAt)}
                    </span>
                  </div>
                )}
                {ticket.resolvedAt && (
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                    <Tag className="h-3.5 w-3.5 text-neutral-400" />
                    <span>
                      Résolu le {formatDateTime(ticket.resolvedAt)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
